import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { cleanCnpj } from "../../../../../utils/format-utils";
import { documentEditService } from "../services/document-edit-service";
import {
  CadastroDocument,
  CadastroEditFormData,
  CadastroSearchFormData,
} from "../types";

export const documentEditKeys = {
  all: ["document-edit"] as const,
  search: (fundName: string, searchParams: string) =>
    [...documentEditKeys.all, "search", fundName, searchParams] as const,
  ops: (fundName: string) =>
    [...documentEditKeys.all, "ops", fundName] as const,
};

interface UseDocumentEditProps {
  fundName: string;
}

export function useDocumentEdit({ fundName }: UseDocumentEditProps) {
  const [cadastroData, setCadastroData] = useState<CadastroDocument[]>([]);
  const [allOps, setAllOps] = useState<string[]>([]);
  const [searchForm, setSearchForm] = useState<CadastroSearchFormData>({
    telefone: "",
    cpfCnpj: "",
  });
  const [editingDocument, setEditingDocument] =
    useState<CadastroDocument | null>(null);
  const [editForm, setEditForm] = useState<CadastroEditFormData>({
    nome_completo: "",
    data_nascimento: "",
    cnpj: "",
    ops: "",
    ifood_uuid: "",
    gateway_pagamento: "",
    service_city: "emissor",
  });

  const searchMutation = useMutation({
    mutationFn: async (searchData: CadastroSearchFormData) => {
      const params: any = { dbName: fundName };

      if (searchData.telefone.trim()) {
        params.telefone = searchData.telefone.replace(/\D/g, "");
      }
      if (searchData.cpfCnpj.trim()) {
        const doc = cleanCnpj(searchData.cpfCnpj);
        if (doc.length === 11) {
          params.cpf = doc;
        } else if (doc.length === 14) {
          params.cnpj = doc;
        }
      }

      return documentEditService.searchCadastro(params);
    },
    onSuccess: (response) => {
      // Handle potential double-wrapped data from backend
      const responseData = (response.data as any)?.data || response.data;
      const cadastroList = responseData?.cadastro || [];
      const opsList = responseData?.allOps || [];

      setCadastroData(Array.isArray(cadastroList) ? cadastroList : []);

      if (opsList.length > 0) {
        const sortedOps = opsList
          .map((item: any) => item.ops)
          .filter((ops: string) => ops)
          .sort((a: string, b: string) => a.localeCompare(b));
        setAllOps(sortedOps);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      documentId,
      formData,
    }: {
      documentId: string;
      formData: CadastroEditFormData;
    }) => {
      const updateData: any = {
        dbName: fundName,
        id: documentId,
      };

      if (formData.nome_completo?.trim()) {
        updateData.nome_completo = formData.nome_completo.trim();
      }
      if (formData.data_nascimento?.trim()) {
        updateData.data_nascimento = formData.data_nascimento.trim();
      }
      if (formData.cnpj?.trim()) {
        updateData.cnpj = formData.cnpj.trim();
      }
      if (formData.ops?.trim()) {
        updateData.ops = formData.ops.trim();
      }
      if (formData.ifood_uuid?.trim()) {
        updateData.ifood_uuid = formData.ifood_uuid.trim();
      }
      if (formData.gateway_pagamento?.trim()) {
        updateData.gateway_pagamento = formData.gateway_pagamento.trim();
      }
      if (
        formData.service_city !== (editingDocument?.service_city ?? "emissor")
      ) {
        updateData.service_city = formData.service_city;
      }

      return documentEditService.updateCadastro(updateData);
    },
    onSuccess: () => {
      searchMutation.mutate(searchForm);
      setEditingDocument(null);
    },
  });

  const handleSearch = () => {
    searchMutation.mutate(searchForm);
  };

  const handleEdit = (document: CadastroDocument) => {
    setEditingDocument(document);
    setEditForm({
      nome_completo: document.nome_completo || "",
      data_nascimento: document.data_nascimento
        ? document.data_nascimento.includes("T")
          ? document.data_nascimento.split("T")[0]
          : document.data_nascimento
        : "",
      cnpj: document.cnpj || "",
      ops: document.ops || "",
      ifood_uuid: document.ifood_uuid || "",
      gateway_pagamento: document.gateway_pagamento || "fitbank",
      service_city: document.service_city ?? "emissor",
    });
  };

  const handleUpdate = () => {
    if (!editingDocument) return;
    updateMutation.mutate({
      documentId: editingDocument.id,
      formData: editForm,
    });
  };

  const handleCancelEdit = () => {
    setEditingDocument(null);
    setEditForm({
      nome_completo: "",
      data_nascimento: "",
      cnpj: "",
      ops: "",
      ifood_uuid: "",
      gateway_pagamento: "",
      service_city: "emissor",
    });
  };

  // Refaz a última busca (depois de bloquear/desbloquear na tabela).
  const refreshSearch = () => {
    if (searchMutation.variables)
      searchMutation.mutate(searchMutation.variables);
  };

  return {
    refreshSearch,
    cadastroData,
    allOps,
    searchForm,
    setSearchForm,
    editingDocument,
    editForm,
    setEditForm,
    handleSearch,
    handleEdit,
    handleUpdate,
    handleCancelEdit,
    isSearching: searchMutation.isPending,
    isUpdating: updateMutation.isPending,
  };
}
